"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { UseQueryResult, useIsMutating } from "@tanstack/react-query";

import { useAppleVaultCheckout } from "@/hooks/useAppleVaultCheckout";
import { FLUID_CDN_URL } from "@/lib/constants";
import { CartResponse } from "@/networking/carts.api";
import usePaypal from "./hooks/usePaypal";
import useBraintreePayment from "./hooks/useBraintreePayment";
import { usePaypalContext } from "@/components/checkout/paypal-provider";
import { useBraintree } from "@/components/checkout/braintree-provider";
import FluidImage from "../images/fluid-image";
import ButtonSlot from "./ButtonSlot";
import DropZone from "@/components/drop-zones/drop-zone";
import {
  addMissingApplePayRecurringSupportBreadcrumb,
  getMissingApplePayRecurringSupportDiagnostic,
  hasRecurringCartItems,
  isApplePayAvailableForCart,
  isApplePayRecurringTestOverrideEnabled,
} from "@/lib/apple-pay-recurring";
import { hasCartErrors, hasCartItemErrors } from "@/lib/api-helpers";
import { checkoutCart } from "@/networking/checkout.api";
import { checkoutRedirect } from "@/lib/checkout-redirect";
import { ErrorAlert } from "@/components/ui/alert";
import { shouldShowFastCheckoutPaymentOptions } from "./fast-checkout-options-visibility";

function Loader() {
  return <div className="h-input w-full animate-pulse rounded bg-gray-200" />;
}

type FastCheckoutOptionsProps = {
  cartToken: string;
  isLoading: boolean;
  paymentMethodsIncludes?: string | null;
  subdomain: string | null;
  query: UseQueryResult<CartResponse | null>;
  kountSessionId?: string;
};

export default function FastCheckoutOptions({
  cartToken,
  isLoading,
  paymentMethodsIncludes,
  subdomain,
  query,
  kountSessionId,
}: FastCheckoutOptionsProps) {
  const { t } = useTranslation();
  const [showAllAPMs, setShowAllAPMs] = useState(false);
  const {
    shouldShowVenmo: ppShowVenmo,
    shouldShowApplePay: shouldShowPPCPApplePay,
  } = usePaypalContext();
  const { shouldShowVenmo: btShowVenmo, deviceData } = useBraintree();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const missingRecurringSupportBreadcrumbRef = useRef<string | null>(null);

  const paypalMethod = query?.data?.cart?.available_payment_methods?.find(
    (method) => method.type === "paypal",
  );
  const isBraintree = paypalMethod?.sdk_type === "braintree";
  const shouldShowVenmo = isBraintree ? btShowVenmo : ppShowVenmo;

  const isSubscriptionPending =
    useIsMutating({
      mutationKey: ["update-item-subscription", cartToken],
    }) > 0;
  const isQuantityPending =
    useIsMutating({
      mutationKey: ["update-item-quantity", cartToken],
    }) > 0;
  const isDiscountPending =
    useIsMutating({
      mutationKey: ["apply-discount", cartToken],
    }) > 0;
  const isRemoveDiscountPending =
    useIsMutating({
      mutationKey: ["remove-discount", cartToken],
    }) > 0;

  const cartData = query?.data;

  const cartHasSubscription = hasRecurringCartItems(cartData?.cart?.items);
  const recurringTestOverride = isApplePayRecurringTestOverrideEnabled();
  const missingRecurringSupportDiagnostic = useMemo(
    () =>
      getMissingApplePayRecurringSupportDiagnostic({
        methods: cartData?.cart?.available_payment_methods,
        hasRecurringItems: cartHasSubscription,
        recurringTestOverride,
      }),
    [
      cartData?.cart?.available_payment_methods,
      cartHasSubscription,
      recurringTestOverride,
    ],
  );

  useEffect(() => {
    if (!missingRecurringSupportDiagnostic) return;

    const breadcrumbKey = `${cartData?.cart?.id ?? cartToken}:${missingRecurringSupportDiagnostic.key}`;
    if (missingRecurringSupportBreadcrumbRef.current === breadcrumbKey) return;

    missingRecurringSupportBreadcrumbRef.current = breadcrumbKey;
    addMissingApplePayRecurringSupportBreadcrumb(
      missingRecurringSupportDiagnostic,
    );
  }, [cartData?.cart?.id, cartToken, missingRecurringSupportDiagnostic]);

  // VGS Apple Pay availability. CreditCard PA is the primary source; a PayPal
  // PA with `decrypted_apple_pay_via_paypal` also routes through the VGS path
  // (backend's select_apple_pay_payment_account falls back to that PA, and
  // CreateAction dispatches on `payment_type: "paypal"`).
  const isVGSApplePayAvailable = isApplePayAvailableForCart({
    methods: cartData?.cart?.available_payment_methods,
    hasRecurringItems: cartHasSubscription,
    recurringTestOverride,
  });

  const isGooglePayAvailable = cartData?.cart?.available_payment_methods?.some(
    (method) => method.type === "credit_card" && method.google_pay === true,
  );

  const isAmazonPayAvailable = cartData?.cart?.available_payment_methods?.some(
    (method) => method.type === "amazon_pay",
  );

  const isPayPalAvailable = cartData?.cart?.available_payment_methods?.some(
    (method) => method.type === "paypal",
  );

  const handleApplePaySuccess = useCallback(async () => {
    setErrorMessage(null);
  }, []);

  const handleApplePayError = useCallback((error: Error) => {
    console.error("VGS Apple Pay failed:", error);
    setErrorMessage(error.message || "Payment failed. Please try again.");
  }, []);

  const handleApplePayRedirect = useCallback((url: string) => {
    window.location.href = url;
  }, []);

  const { applePaySupported, isProcessing, handleAppleVaultCheckout } =
    useAppleVaultCheckout({
      paymentMethodsIncludes,
      deviceData: deviceData ?? undefined,
      kountSessionId,
      onSuccess: handleApplePaySuccess,
      onError: handleApplePayError,
      onRedirect: handleApplePayRedirect,
    });

  // Priority: VGS Apple Pay > PPCP Apple Pay
  const shouldShowVGSApplePay = isVGSApplePayAvailable && applePaySupported;
  const shouldShowPPCPApplePayFinal =
    !shouldShowVGSApplePay && shouldShowPPCPApplePay;
  const shouldShowPaymentOptions = shouldShowFastCheckoutPaymentOptions(
    cartData?.cart,
  );

  const handleApplePayPress = () => {
    setErrorMessage(null);
    handleAppleVaultCheckout(cartToken);
  };

  const handlePaypalSuccess = useCallback(async () => {
    // Fast checkout success is handled by usePayPalCheckout hook
  }, []);

  const handlePaypalError = useCallback((error: Error) => {
    console.error("PayPal payment failed:", error);
  }, []);

  const handlePaypalRedirect = useCallback((url: string) => {
    window.location.href = url;
  }, []);

  const isFastCheckoutDisabled =
    isProcessing ||
    isSubscriptionPending ||
    isQuantityPending ||
    isDiscountPending ||
    isRemoveDiscountPending ||
    hasCartErrors(query.data?.cart ?? null) ||
    hasCartItemErrors(query.data?.cart ?? null);

  const {
    paypalButton: ppcpPaypalButton,
    venmoButton: ppcpVenmoButton,
    payLaterButton: ppcpPayLaterButton,
    applePayButton,
  } = usePaypal({
    fastCheckout: true,
    cartToken,
    disabled: isFastCheckoutDisabled,
    onSuccess: handlePaypalSuccess,
    onError: handlePaypalError,
    onRedirect: handlePaypalRedirect,
    paymentMethodsIncludes,
    deviceData: deviceData ?? undefined,
    kountSessionId,
  });

  const handleBraintreeSuccess = useCallback(async () => {
    const checkoutResult = await checkoutCart(cartToken);
    checkoutRedirect(checkoutResult, cartToken);
  }, [cartToken]);

  const handleBraintreeError = useCallback((error: string) => {
    setErrorMessage(error);
  }, []);

  const {
    paypalButton: btPaypalButton,
    venmoButton: btVenmoButton,
    payLaterButton: btPayLaterButton,
    payLaterMessage: btPayLaterMessage,
  } = useBraintreePayment({
    fastCheckout: true,
    cartToken,
    disabled: isFastCheckoutDisabled,
    onSuccess: handleBraintreeSuccess,
    onError: handleBraintreeError,
    paymentMethodsIncludes,
  });

  const paypalButton = isBraintree ? btPaypalButton : ppcpPaypalButton;
  const venmoButton = isBraintree ? btVenmoButton : ppcpVenmoButton;
  const payLaterButton = isBraintree ? btPayLaterButton : ppcpPayLaterButton;
  const payLaterMessage = isBraintree ? btPayLaterMessage : null;

  const showFastCheckoutOptions =
    shouldShowPaymentOptions &&
    (shouldShowVGSApplePay ||
      shouldShowPPCPApplePayFinal ||
      isGooglePayAvailable ||
      isAmazonPayAvailable ||
      isPayPalAvailable);

  if (!showFastCheckoutOptions && !isLoading) {
    return null;
  }

  const availableButtons = [];

  // VGS Apple Pay (priority #1)
  if (shouldShowPaymentOptions && shouldShowVGSApplePay) {
    availableButtons.push({
      key: "apple-pay-vgs",
      element: (
        <button
          type="button"
          className="h-input flex w-full items-center justify-center rounded bg-black py-2 text-white transition-colors duration-200 hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={handleApplePayPress}
          disabled={isFastCheckoutDisabled}
          data-testid="apple-pay-button"
        >
          <span className="sr-only">{t("pay_with_apple_pay")}</span>
          {isProcessing ? (
            <div className="h-5 w-5 animate-spin rounded-full border-4 border-t-4 border-white border-t-transparent" />
          ) : (
            <img
              src={`${FLUID_CDN_URL}/images/checkout/icons/ApplePayWhite.png`}
              alt=""
              width={50}
              height={50}
              className="z-40 fill-white text-white"
            />
          )}
        </button>
      ),
    });
  }

  // PPCP Apple Pay (priority #2 - only if VGS not available)

  if (
    shouldShowPaymentOptions &&
    shouldShowPPCPApplePayFinal &&
    applePayButton
  ) {
    availableButtons.push({
      key: "apple-pay-ppcp",
      element: applePayButton,
    });
  }

  if (shouldShowPaymentOptions && isGooglePayAvailable) {
    availableButtons.push({
      key: "google-pay",
      element: (
        <button
          type="button"
          className="h-input flex w-full items-center justify-center rounded border border-transparent bg-gray-800 py-2 text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={isFastCheckoutDisabled}
        >
          <span className="sr-only">{t("pay_with_google_pay")}</span>
          <img
            src={`${FLUID_CDN_URL}/images/checkout/icons/GooglePay.png`}
            alt=""
            width={50}
            height={50}
            className="z-40"
          />
        </button>
      ),
    });
  }

  if (shouldShowPaymentOptions && isAmazonPayAvailable) {
    availableButtons.push({
      key: "amazon-pay",
      element: (
        <button
          type="button"
          className="h-input flex w-full items-center justify-center rounded border border-transparent bg-yellow-300 py-2 text-white hover:bg-yellow-400 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={isFastCheckoutDisabled}
        >
          <span className="sr-only">{t("pay_with_amazon_pay")}</span>
          <img
            src={`${FLUID_CDN_URL}/images/checkout/icons/AmazonPay.png`}
            alt=""
            width={45}
            height={45}
            className="black-icons z-40"
          />
        </button>
      ),
    });
  }

  if (shouldShowPaymentOptions && isPayPalAvailable && paypalButton) {
    availableButtons.push({
      key: "paypal",
      element: paypalButton,
    });
  }

  if (
    shouldShowPaymentOptions &&
    isPayPalAvailable &&
    shouldShowVenmo &&
    venmoButton
  ) {
    availableButtons.push({
      key: "venmo",
      element: venmoButton,
    });
  }

  if (shouldShowPaymentOptions && isPayPalAvailable && payLaterButton) {
    availableButtons.push({
      key: "paylater",
      element: payLaterButton,
    });
  }

  if (availableButtons.length === 0 && !isLoading) {
    return null;
  }

  const maxVisibleButtons = 3;
  const hasMoreThanMax = availableButtons.length > maxVisibleButtons;
  const visibleButtons = showAllAPMs
    ? availableButtons
    : availableButtons.slice(0, maxVisibleButtons);

  const renderButtonGrid = (buttons: typeof visibleButtons) => {
    const buttonRows: Array<typeof buttons> = [];
    for (let i = 0; i < buttons.length; i += 3) {
      buttonRows.push(buttons.slice(i, i + 3));
    }
    return (
      <div className="mt-6 space-y-3">
        {buttonRows.map((row, rowIndex) => {
          return (
            <div key={rowIndex} className="flex gap-3">
              {row.map((button) => (
                <ButtonSlot key={button.key} className="min-w-0 flex-1">
                  {isLoading ? (
                    <div className="h-input">
                      <Loader />
                    </div>
                  ) : (
                    button.element
                  )}
                </ButtonSlot>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <DropZone
        zone="above_fast_checkout"
        token={cartToken}
        subdomain={subdomain ?? null}
        page="checkout"
        companyId={query?.data?.cart?.company?.id}
      />

      {renderButtonGrid(visibleButtons)}

      {isPayPalAvailable && payLaterMessage}

      {errorMessage && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3">
          <div className="flex">
            <div className="text-sm text-red-700">{errorMessage}</div>
          </div>
        </div>
      )}

      {hasMoreThanMax && !showAllAPMs && (
        <div className="mt-3 flex justify-center gap-2">
          <button
            type="button"
            className="flex items-center gap-2 text-sm"
            onClick={() => setShowAllAPMs(true)}
          >
            {t("show_more_options")}
            <FluidImage
              src={`${FLUID_CDN_URL}/images/checkout/icons/chevron-down.svg`}
              alt="Arrow Down"
              width={12}
              height={12}
            />
          </button>
        </div>
      )}

      {hasMoreThanMax && showAllAPMs && (
        <div className="mt-3 flex justify-center gap-2">
          <button
            type="button"
            className="flex items-center gap-2 text-sm"
            onClick={() => setShowAllAPMs(false)}
          >
            {t("hide_more_options")}
            <FluidImage
              src={`${FLUID_CDN_URL}/images/checkout/icons/chevron-up.svg`}
              alt="Arrow Up"
              width={12}
              height={12}
            />
          </button>
        </div>
      )}

      {visibleButtons.length > 0 && hasCartErrors(query.data?.cart ?? null) && (
        <ErrorAlert
          message={(query.data?.cart?.messages?.errors || []).join(", ")}
          className="mt-4"
        />
      )}
      {visibleButtons.length > 0 &&
        hasCartItemErrors(query.data?.cart ?? null) && (
          <ErrorAlert message={t("fix_cart_items_message")} className="mt-4" />
        )}

      {visibleButtons.length > 0 && (
        <div className="relative mt-6">
          <div
            aria-hidden="true"
            className="absolute inset-0 flex items-center"
          >
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-4 text-xs text-gray-600">
              {t("or")}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
